import {
	BUYER_ADMIN_ONLY,
	SUPPLIER_ADMIN_ONLY,
	BUYER_ROLES,
	SUPPLIER_ROLES,
	BUYER_ADMIN,
	SUPPLIER_ADMIN,
} from '../components/roles/roles';
import { nullSafeGet, isAdminUser, compareStrings } from './DataAccessUtils';
import { ROLE_TYPES } from './DataConstants';
import {
	PERMISSION_NAMES,
	getBuyerConfigForPermission,
	getSupplierConfigForPermission,
	getRolePermissionKeyMap,
	isUserAllowedToAccess,
	getBuyerCompanySettingValue,
} from './AuthUtils';

export const BRAND_ASSET_GROUP_FEATURE_FLAG = 'brand_asset_group_enabled';

const ADDRESS_FIELDS = [
	'streetAddress1',
	'streetAddress2Opt',
	'city',
	'region',
	'postcode',
	'country',
];

export const setAddressFormFields = (form, prefix, inputFieldName, address) => {
	const fields = {};
	if (inputFieldName) {
		fields[inputFieldName] = address;
	}
	ADDRESS_FIELDS.forEach((field) => {
		fields[`${prefix}-${field}`] = address ? address[field] : undefined;
	});
	form.setFieldsValue(fields);
};

export const getAddressFromFormFields = (values, prefix) => {
	return ADDRESS_FIELDS.reduce((acc, field) => {
		acc[field] = values[`${prefix}-${field}`];
		return acc;
	}, {});
};

export const getAddressFromForm = (form, prefix) => {
	return ADDRESS_FIELDS.reduce((acc, field) => {
		acc[field] = form.getFieldValue(`${prefix}-${field}`);
		return acc;
	}, {});
};

export const addressValidator = (rule, value, callback, source, options, values: any = {}) => {
	const { streetAddress1, city, region, country } = values;
	if (!value || !streetAddress1 || !city || !region || !country) {
		callback('Please search for and select an valid address');
	} else {
		callback();
	}
};

export const addressValidatorFormv3 = (form) => (rule, value, callback, source, options) => {
	const streetAddress1 = form.getFieldValue('primaryAddress-streetAddress1');
	const city = form.getFieldValue('primaryAddress-city');
	const region = form.getFieldValue('primaryAddress-region');
	const country = form.getFieldValue('primaryAddress-country');
	addressValidator(rule, value, callback, source, options, {
		streetAddress1,
		city,
		region,
		country,
	});
};

/*
	Same completeness check as `addressValidatorFormv3`, but for forms where the address is genuinely
	optional.

	`addressValidator` rejects on `!value`, and rc-form runs a custom `validator` even when the rule
	is `required: false` — so on those forms an untouched, empty address input fails validation and
	takes every *other* field on the form down with it. That is OPE-5248: a supplier's primary
	contact email could not be saved without first re-picking an address in the autocomplete, which
	is exactly what the internal team hits when correcting an email on a supplier that has no address
	on file.

	Here an empty input passes; only an address the user actually selected has to be complete. This
	is the semantic `addressValidatorFormv4` already has for antd v4 forms — v3 is the outlier.
*/
export const optionalAddressValidatorFormv3 =
	(form) => (rule, value, callback, source, options) => {
		if (!value) {
			callback();
			return;
		}
		addressValidatorFormv3(form)(rule, value, callback, source, options);
	};

export const addressValidatorFormv4 = (getFieldValue) => (_, value) => {
	const streetAddress1 = getFieldValue('primaryAddress-streetAddress1');
	const city = getFieldValue('primaryAddress-city');
	const region = getFieldValue('primaryAddress-region');
	const country = getFieldValue('primaryAddress-country');
	if (value && (!streetAddress1 || !city || !region || !country)) {
		return Promise.reject(new Error('Please search for and select a valid address'));
	} else {
		return Promise.resolve();
	}
};

export const getCompanyWideDocumentPermissions = (userType: string, companyConfig: any) => {
	const getConfigForPermission = (permissionName: string) => {
		return userType === ROLE_TYPES.BUYER
			? getBuyerConfigForPermission(permissionName, companyConfig)
			: getSupplierConfigForPermission(permissionName, companyConfig);
	};

	const accessConfig = getConfigForPermission(PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS) || {};
	const modifyConfig = getConfigForPermission(PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS) || {};

	const accessPermissions: any = {};
	const allKeys = new Set([...Object.keys(accessConfig), ...Object.keys(modifyConfig)]);
	allKeys.forEach((key) => {
		if (accessConfig[key] === true || modifyConfig[key] === true) {
			accessPermissions[key] = true;
		}
	});

	const modifyPermissions: any = {};
	Object.keys(modifyConfig).forEach((key) => {
		if (modifyConfig[key] === true) {
			modifyPermissions[key] = true;
		}
	});

	return {
		access: accessPermissions,
		modify: modifyPermissions,
	};
};

export const mergeDocumentPermissions = (documentPerms: any, companyPerms: any) => {
	if (!documentPerms) {
		return {
			access: { ...(companyPerms.access || {}) },
			modify: { ...(companyPerms.modify || {}) },
		};
	}

	return {
		access: {
			...(companyPerms.access || {}),
			...(documentPerms.access || {}),
		},
		modify: {
			...(companyPerms.modify || {}),
			...(documentPerms.modify || {}),
		},
	};
};

export const isDocumentCreator = (document, currentUser) => {
	const documentCreatedBy = nullSafeGet('createdBy', document);
	const userEmail = nullSafeGet('email', currentUser);
	return compareStrings(documentCreatedBy, userEmail);
};

export const isRoleAllowedForDocumentPermission = (
	permissions: any,
	userType: string,
	role: string,
	permissionType: 'access' | 'modify'
) => {
	if (!permissions) {
		return false;
	}

	const rolePermissionMap = getRolePermissionKeyMap(userType);
	const permissionKey = rolePermissionMap[role];
	if (!permissionKey) {
		return false;
	}

	const permissionConfig = nullSafeGet(permissionType, permissions) || {};
	return permissionConfig[permissionKey] === true;
};

export const canRoleModifyDocumentFromPermissions = (
	permissions: any,
	userType: string,
	role: string
) => {
	return isRoleAllowedForDocumentPermission(permissions, userType, role, 'modify');
};

export const canRoleAccessDocumentFromPermissions = (
	permissions: any,
	userType: string,
	role: string
) => {
	if (!permissions) {
		return false;
	}

	return (
		isRoleAllowedForDocumentPermission(permissions, userType, role, 'access') ||
		isRoleAllowedForDocumentPermission(permissions, userType, role, 'modify')
	);
};

const isRoleExplicitlyDenied = (permissionConfig, userType, roles = []) => {
	if (!permissionConfig) {
		return false;
	}

	const roleName = getRolePermissionKeyMap(userType);

	return roles.some((role) => {
		const permissionKey = roleName[role];
		return (
			permissionKey &&
			Object.prototype.hasOwnProperty.call(permissionConfig, permissionKey) &&
			permissionConfig[permissionKey] === false
		);
	});
};

const checkRoleAccess = (permissionConfig, userType, roles = []) => {
	if (!permissionConfig) {
		return false;
	}

	if (roles.some((role) => BUYER_ADMIN_ONLY.has(role) || SUPPLIER_ADMIN_ONLY.has(role))) {
		return true;
	}

	const roleName = getRolePermissionKeyMap(userType);
	return roles.map((_) => nullSafeGet(`${roleName[_]}`, permissionConfig)).some((_) => !!_);
};

const canUserAccessDocument = (
	document,
	currentUser,
	companySettings,
	permissionType: 'access' | 'modify',
	fallbackPermissionNames: string[]
) => {
	const userType = nullSafeGet('userType', currentUser);
	const roles = nullSafeGet('roles', currentUser) || [];

	if (isDocumentCreator(document, currentUser)) {
		return true;
	}

	let permissions;
	if (userType === ROLE_TYPES.BUYER) {
		permissions = nullSafeGet('buyerPermissions', document);
	} else if (userType === ROLE_TYPES.SUPPLIER) {
		permissions = nullSafeGet('supplierPermissions', document);
	}

	const permissionConfig = permissions && nullSafeGet(permissionType, permissions);

	if (permissionConfig) {
		if (isRoleExplicitlyDenied(permissionConfig, userType, roles)) {
			return false;
		}

		if (checkRoleAccess(permissionConfig, userType, roles)) {
			return true;
		}
	}

	return fallbackPermissionNames.some((permissionName) =>
		isUserAllowedToAccess(userType)(permissionName, companySettings, roles)
	);
};

export const canUserViewDocument = (document, currentUser, companySettings) => {
	return canUserAccessDocument(document, currentUser, companySettings, 'access', [
		PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS,
		PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS,
	]);
};

export const canUserEditDocument = (document, currentUser, companySettings) => {
	return canUserAccessDocument(document, currentUser, companySettings, 'modify', [
		PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS,
	]);
};

export const canUserDeleteDocument = (document, currentUser) => {
	return isDocumentCreator(document, currentUser) || isAdminUser(currentUser);
};

export const getDocumentPermissionsByUserType = (document, userType: string) => {
	if (userType === ROLE_TYPES.BUYER) {
		return nullSafeGet('buyerPermissions', document);
	}
	return nullSafeGet('supplierPermissions', document);
};

export const getMergedDocumentPermissions = (document, userType: string, companyConfig: any) => {
	const companyWidePermissions = getCompanyWideDocumentPermissions(userType, companyConfig);
	const documentPermissions = getDocumentPermissionsByUserType(document, userType);
	return mergeDocumentPermissions(documentPermissions, companyWidePermissions);
};

export const normalizePermissionsForAllRoles = (userType: string, permissionsToNormalize) => {
	const rolePermissionMap = getRolePermissionKeyMap(userType);
	const allRoles = userType === ROLE_TYPES.SUPPLIER ? SUPPLIER_ROLES : BUYER_ROLES;
	const availableRoles = allRoles.filter((role) => role !== BUYER_ADMIN && role !== SUPPLIER_ADMIN);

	const currentAccess = nullSafeGet('access', permissionsToNormalize) || {};
	const currentModify = nullSafeGet('modify', permissionsToNormalize) || {};

	const normalizedPermissions = {
		access: {},
		modify: {},
	};

	availableRoles.forEach((role) => {
		const permissionKey = rolePermissionMap[role];
		if (!permissionKey) {
			return;
		}
		normalizedPermissions.access[permissionKey] = !!currentAccess[permissionKey];
		normalizedPermissions.modify[permissionKey] = !!currentModify[permissionKey];
	});

	return normalizedPermissions;
};

export const getLocationBillToEntity = (location) => nullSafeGet('invoicesBillTo.billTo', location);

// Bill to entities are a buyer company opt-in. The setting belongs to the buyer company that owns
// the location, so prefer that when the payload carries it -- a third party supplier serves many
// buyer companies and has no company settings of its own to resolve against. Falls back to the
// viewing user's settings, which is what buyers and internal techs resolve through today.
export const isLocationBillToVisible = (currentUser, companyConfig, location) => {
	const locationBuyerCompanyConfig = nullSafeGet(
		'buyerCompany.buyerCompanySettings.config',
		location
	);
	if (locationBuyerCompanyConfig) {
		return !!nullSafeGet(
			'locationConfig.enableBillToAddressesForLocation',
			locationBuyerCompanyConfig
		);
	}

	return !!getBuyerCompanySettingValue(nullSafeGet('userType', currentUser))(
		'locationConfig.enableBillToAddressesForLocation',
		companyConfig,
		currentUser
	);
};

/**
 * Decimal places kept for a latitude/longitude. Six is ~0.1 m -- finer than any pin a person can
 * place, it fits the `Float` columns behind these fields, and it keeps the geocoder's 13-decimal
 * output from overflowing the coordinate inputs.
 */
const COORDINATE_DECIMALS = 6;

export const roundCoordinate = (value) => {
	// `Number(null)` and `Number('')` are both 0, which would silently drop a pin in the Gulf of
	// Guinea, so reject empties before converting.
	if (value === null || value === undefined || value === '') {
		return undefined;
	}
	const coordinate = Number(value);
	return isFinite(coordinate) ? Number(coordinate.toFixed(COORDINATE_DECIMALS)) : undefined;
};

const LATITUDE_LIMIT = 90;
const LONGITUDE_LIMIT = 180;

/**
 * Parses a coordinate, or null when it is not one. Both guards matter: `Number(' ')` is 0, so an
 * all-whitespace field would place a pin in the Gulf of Guinea, and Mapbox throws on a latitude
 * beyond +/-90.
 */
const toCoordinate = (value, limit) => {
	if (value === null || value === undefined) {
		return null;
	}
	const text = String(value).trim();
	if (text === '') {
		return null;
	}
	const coordinate = Number(text);
	if (!isFinite(coordinate) || Math.abs(coordinate) > limit) {
		return null;
	}
	return coordinate;
};

export const toLatitude = (value) => toCoordinate(value, LATITUDE_LIMIT);

export const toLongitude = (value) => toCoordinate(value, LONGITUDE_LIMIT);
